/**
 * Copyright (c) 2021-2024 Kognitos, Inc. All rights reserved.
 */
import { DownOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import classnames from 'classnames';
import React, { useState } from 'react';

// Local imports
import PortalContainer from '@/components/PortalContainer';
import UserMenu from '@/components/UserMenu/UserMenu';
import DepartmentSelectorMenu from '@/components/department/DepartmentSelectorMenu';
import AccountIcon from '@/components/icons/AccountIcon';
import TopHeaderLogoIcon from '@/components/icons/TopHeaderLogoIcon';
import { useApp } from '@/provider/app';

// styles and icons
import { Department } from '@/generated/graphql';
import styles from './AppTopHeader.module.less';

enum ActionComponents {
  ACCOUNT = 'ACCOUNT',
  DEPARTMENT_SELECTOR = 'DEPARTMENT_SELECTOR'
}

export const HEADER_HEIGHT = 42;

const DeptTooltip = ({ department }: { department: Department | null }) => {
  return department ? (
    <span className={styles.departmentToolTip}>{department?.name || ''}</span>
  ) : null;
};

function AppTopHeader() {
  const [actionComponent, setActionComponent] = useState<string | undefined>();
  const [showTooltip, setShowTooltip] = useState<boolean>(false);

  const [departments, setDepartments] = useState<Department[]>([]);
  const [departmentSearchText, setDepartmentSearchText] = useState('');

  const { currentDepartment: department } = useApp();
  let depName = department?.name || '';
  if (depName.length > 15) {
    depName = `${depName.substring(0, 15)}...`;
  }

  const handleMenuItemClicked = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    e.stopPropagation();
    setActionComponent((prev) => {
      if (prev !== id) {
        return id;
      }
      return undefined;
    });
    setShowTooltip(false);
  };

  return (
    <div>
      <div className={styles.container}>
        <div className={styles.pageName}>
          <TopHeaderLogoIcon />
        </div>
        <div className={styles.rightContainer}>
          <Tooltip
            title={<DeptTooltip department={department} />}
            placement="bottom"
            open={showTooltip}
          >
            <button
              data-cy="top-nav-dept-selector"
              type="button"
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
              className={classnames([
                styles.DepartmentSelectorButton,
                {
                  [styles.DepartmentSelectorButtonActive]:
                    actionComponent === ActionComponents.DEPARTMENT_SELECTOR
                }
              ])}
              onClick={(e) =>
                handleMenuItemClicked(e, ActionComponents.DEPARTMENT_SELECTOR)
              }
            >
              <span data-cy="selected-department-name">{depName}</span>
              <DownOutlined style={{ marginLeft: 12 }} />
            </button>
          </Tooltip>
          <div className={styles.divider} />
          <span
            data-cy="nav-ACCOUNT"
            className={styles.iconContainer}
            onClick={(e) => handleMenuItemClicked(e, ActionComponents.ACCOUNT)}
            title="Account"
          >
            <AccountIcon />
          </span>
        </div>
      </div>
      {actionComponent === ActionComponents.ACCOUNT ? (
        <PortalContainer
          style={{
            top: HEADER_HEIGHT + 2,
            right: 12
          }}
          hide={() => setActionComponent(undefined)}
        >
          <UserMenu hide={() => setActionComponent(undefined)} />
        </PortalContainer>
      ) : null}

      {actionComponent === ActionComponents.DEPARTMENT_SELECTOR ? (
        <PortalContainer
          style={{
            top: HEADER_HEIGHT + 2,
            right: 56
          }}
          hide={() => setActionComponent(undefined)}
        >
          <DepartmentSelectorMenu
            departments={departments}
            setDepartments={setDepartments}
            departmentSearchText={departmentSearchText}
            setDepartmentSearchText={setDepartmentSearchText}
            hide={() => setActionComponent(undefined)}
          />
        </PortalContainer>
      ) : null}
    </div>
  );
}

export default AppTopHeader;
